import React, { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";

// 12 hand-curated slides — monuments, experiences and festivals of India
// with Himalayan (Nepal), desert (Dubai) and tropical (Thailand) extensions.
// Every image URL is a verified Pexels/Unsplash asset matching its caption.
const SLIDES = [
  {
    src: "https://images.pexels.com/photos/36113903/pexels-photo-36113903.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "India",
    title: "Taj Mahal at Sunset",
    place: "Agra · Uttar Pradesh",
    caption: "The world's greatest love letter, glowing amber under a Yamuna sky — Shah Jahan's marble poetry to Mumtaz.",
  },
  {
    src: "https://images.pexels.com/photos/6148088/pexels-photo-6148088.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "India",
    title: "Yoga on the Ganges",
    place: "Rishikesh · Uttarakhand",
    caption: "Sunrise sadhana on the sacred Ganga — India's yoga capital where the Himalayas meet meditation.",
  },
  {
    src: "https://images.unsplash.com/photo-1508817628294-5a453fa0b8fb?auto=format&fit=crop&q=85&w=1920&h=1080",
    country: "India",
    title: "Wildlife Safari — Royal Bengal Tiger",
    place: "Ranthambore · Bandhavgarh",
    caption: "Track India's most elusive predator through ancient jungles and ruined forts — the striped king in his kingdom.",
  },
  {
    src: "https://images.pexels.com/photos/11511275/pexels-photo-11511275.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "India",
    title: "Holi — Festival of Colours",
    place: "Mathura · Vrindavan",
    caption: "A million clouds of gulal fill the air — Krishna's homeland becomes the wildest, warmest street party on earth.",
  },
  {
    src: "https://images.pexels.com/photos/26833726/pexels-photo-26833726.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "India",
    title: "Diwali — Festival of Lights",
    place: "Across India",
    caption: "A million diyas illuminate homes, temples and hearts — India's most luminous celebration of light over darkness.",
  },
  {
    src: "https://images.pexels.com/photos/32155068/pexels-photo-32155068.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "India",
    title: "Amber Fort",
    place: "Jaipur · Rajasthan",
    caption: "Honey-hued ramparts rise above Maota Lake — a Rajput fortress of mirrored halls and marble courtyards.",
  },
  {
    src: "https://images.pexels.com/photos/35077286/pexels-photo-35077286.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "India",
    title: "City Palace, Udaipur",
    place: "Udaipur · Rajasthan",
    caption: "Palace domes drift above Lake Pichola at dusk — Rajasthan's most romantic royal skyline.",
  },
  {
    src: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&q=85&w=1920&h=1080",
    country: "India",
    title: "Kerala Backwaters",
    place: "Alleppey · Kerala",
    caption: "Houseboats drift through palm-lined canals of God's own country — coconut groves, kingfishers and slow time.",
  },
  {
    src: "https://images.pexels.com/photos/28556164/pexels-photo-28556164.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "India",
    title: "Ganga Aarti",
    place: "Varanasi · Uttar Pradesh",
    caption: "Lamps of fire offered to the eternal Ganga at Dashashwamedh Ghat — the world's oldest living city at prayer.",
  },
  {
    src: "https://images.pexels.com/photos/29517656/pexels-photo-29517656.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "India",
    title: "Khajuraho Temples",
    place: "Khajuraho · Madhya Pradesh",
    caption: "1,000-year-old UNESCO temples covered in intricate carvings — the sensual mithuna sculptures that inspired the Kama Sutra, celebrated as art, not taboo.",
  },
  {
    src: "https://images.pexels.com/photos/32858802/pexels-photo-32858802.png?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "Nepal",
    title: "Boudhanath Stupa",
    place: "Kathmandu · Nepal",
    caption: "Prayer flags flutter over the world's largest stupa — Nepal's Buddhist heart in the shadow of the Himalayas.",
  },
  {
    src: "https://images.pexels.com/photos/19790158/pexels-photo-19790158.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "Dubai",
    title: "Burj Al Arab",
    place: "Dubai · UAE",
    caption: "The sail on the Arabian Gulf — Dubai's seven-star icon glowing over Jumeirah at golden hour.",
  },
  {
    src: "https://images.pexels.com/photos/11105014/pexels-photo-11105014.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
    country: "Thailand",
    title: "Grand Palace, Bangkok",
    place: "Bangkok · Thailand",
    caption: "Golden spires and emerald Buddhas — Bangkok's radiant heart of Thai royal heritage.",
  },
];

export default function DestinationsSlider() {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "center" },
    [Autoplay({ delay: 4500, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSel = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSel);
    onSel();
    return () => emblaApi?.off("select", onSel);
  }, [emblaApi]);

  return (
    <section
      className="py-24 md:py-32 bg-peacock text-white relative overflow-hidden"
      data-testid="destinations-slider"
    >
      <div className="container-tti mb-12">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4 text-marigold">A photographic journey · Four countries</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] text-white">
              Monuments, festivals
              <br />
              <span className="italic text-marigold">and everyday magic.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-cream/80 text-lg font-light leading-relaxed">
              From Rajasthan&apos;s forts and Kerala&apos;s backwaters to Rishikesh yoga and Holi colours — plus
              Himalayan, desert and tropical escapes to Nepal, Dubai and Thailand.
            </p>
          </div>
        </div>
      </div>

      {/* Carousel */}
      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef} data-testid="destinations-carousel">
          <div className="flex">
            {SLIDES.map((s, i) => (
              <div
                key={i}
                className="relative flex-[0_0_92%] sm:flex-[0_0_75%] md:flex-[0_0_62%] lg:flex-[0_0_55%] xl:flex-[0_0_48%] mx-3"
                data-testid={`destination-slide-${i}`}
              >
                <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden rounded-sm bg-black">
                  <img
                    src={s.src}
                    alt={`${s.title} — ${s.place}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute top-5 left-5 md:top-6 md:left-6">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] bg-saffron/90 text-white px-3 py-1.5 rounded-sm backdrop-blur">
                      <MapPin className="w-3 h-3" />
                      {s.country}
                    </span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-6 md:p-10">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-marigold mb-2">{s.place}</div>
                    <h3 className="font-serif text-3xl md:text-4xl text-white tracking-tight leading-tight mb-2">
                      {s.title}
                    </h3>
                    <p className="text-white/85 text-sm md:text-[15px] font-light max-w-md leading-relaxed">
                      {s.caption}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Nav buttons */}
        <button
          aria-label="Previous destination"
          onClick={() => emblaApi?.scrollPrev()}
          data-testid="destinations-prev"
          className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur border border-cream-200 items-center justify-center text-foreground hover:bg-saffron hover:text-white transition-all shadow-md"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          aria-label="Next destination"
          onClick={() => emblaApi?.scrollNext()}
          data-testid="destinations-next"
          className="hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 backdrop-blur border border-cream-200 items-center justify-center text-foreground hover:bg-saffron hover:text-white transition-all shadow-md"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Dots */}
      <div className="container-tti mt-10 flex items-center justify-between">
        <div className="flex gap-2" data-testid="destinations-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`Go to destination ${i + 1}`}
              data-testid={`destinations-dot-${i}`}
              className={`h-1.5 rounded-sm transition-all ${
                i === selected ? "w-10 bg-marigold" : "w-4 bg-white/30 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-white/50">
          {String(selected + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
        </div>
      </div>
    </section>
  );
}
